/**
 * Permission combinations a single principal (role or user) may never hold at
 * the same time. Mirrors `enforceSoxSegregation` in `@kc3/auth`, which rejects
 * the save server-side — the UI blocks the combination up front so an admin
 * never picks a set that cannot be persisted.
 */
export const MUTUALLY_EXCLUSIVE_PERMISSIONS: ReadonlyArray<readonly [string, string]> = [
  ['billing:manage', 'sox:approve'],
]

/** Keys that cannot coexist with `key`. */
export function conflictingPermissionKeys(key: string): string[] {
  return MUTUALLY_EXCLUSIVE_PERMISSIONS.flatMap(([a, b]) =>
    key === a ? [b] : key === b ? [a] : [],
  )
}

/**
 * Why `key` cannot be selected given the currently-selected keys, or `null`
 * when there is no conflict. Suitable as a checkbox tooltip.
 */
export function permissionConflictReason(
  key: string,
  selectedKeys: ReadonlySet<string>,
): string | null {
  const blocker = conflictingPermissionKeys(key).find((k) => selectedKeys.has(k))
  return blocker
    ? `Segregation of duties: "${key}" cannot be combined with "${blocker}".`
    : null
}

/**
 * Drop one side of every conflicting pair so a bulk "select all" produces a set
 * the backend will actually accept. Both keys stay individually selectable.
 */
export function withoutPermissionConflicts(keys: readonly string[]): string[] {
  const set = new Set(keys)
  for (const [a, b] of MUTUALLY_EXCLUSIVE_PERMISSIONS) {
    if (set.has(a) && set.has(b)) set.delete(b)
  }
  return [...set]
}
